import { useChartPeriod } from '@/hooks';
import { tokensMapping } from '@/utils/tokens';
import {
  CurrencyValue,
  formatNumber,
  LineChart,
  PercentageValue
} from '@blockanalitica/ui';
import type { SimpleChartData } from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import { ReactNode, useCallback } from 'react';

export default function HistoricGraphs({
  data,
  type,
  collateralSymbol,
  collateralAddress,
  timeFilter,
  chartDaysAgo
}: {
  data: SimpleChartData[];
  type: string;
  collateralSymbol: string;
  collateralAddress: string;
  timeFilter?: ReactNode;
  chartDaysAgo: string;
}) {
  const [, updateChartConfigWithPeriod] = useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod();

  const valueFormatter = useCallback(
    (value: number) => {
      switch (type) {
        case 'ink':
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: {
                  name: collateralSymbol,
                  address: collateralAddress
                },
                iconSize: '3'
              }}
            />
          );
        case 'weighted_cr':
          return <PercentageValue value={value} />;
        case 'revenue':
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: 'USD'
              }}
            />
          );
        case 'total_exit_fees':
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: tokensMapping('MKR'),
                iconSize: '3'
              }}
            />
          );
        default:
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: tokensMapping('USDS'),
                iconSize: '3'
              }}
            />
          );
      }
    },
    [type, collateralAddress, collateralSymbol]
  );

  const yScaleValueFormatter = useCallback(
    (value: number) => formatNumber(value),
    []
  );

  return (
    <LineChart
      key={type}
      data={data}
      datasets={[
        {
          xField: 'date',
          yField: type
        }
      ]}
      valueFormatter={valueFormatter}
      yScaleValueFormatter={yScaleValueFormatter}
      timeFilter={timeFilter}
      config={config}
    />
  );
}
